/**
 * Client for the in-place check correction callable (#314).
 *
 * The callable takes a `checkId` and the changed fields only — the clinic is
 * resolved server-side off the check doc, so nothing here can widen what the
 * caller is allowed to touch.
 */
import { httpsCallable, type FunctionsError } from 'firebase/functions'
import { functions } from '../config'
import type { CorrectionPayload } from '../utils/checkCorrection'

export type CorrectCheckOutcome =
  /** The typed values matched what the check already asked — no call spent. */
  | { kind: 'no_change'; message?: string }
  /** A corrected check was fired (or an identical in-flight one reused). */
  | { kind: 'fired'; checkId: string; flowId: string; terminal: string }

/** The correction could not be run at all — the operator's edit is still on
 *  screen and the message says what to fix. */
export class CorrectCheckUnrunnable extends Error {}

export async function correctAndRecheck(
  orgId: string,
  checkId: string,
  /** The changed fields — plus a payer pick's ids, which make the payer
   *  authoritative on the backend (`withPayerPick`, #349). */
  correction: CorrectionPayload,
  /**
   * Ask the payer the same question again with nothing edited (#349 A6).
   *
   * An empty correction is otherwise a no-op by definition — right for the
   * automated loop, wrong for an operator pressing "Re-check" because the payer
   * was down when this ran. Spends a real payer call, so it is never implied:
   * only the button sets it.
   */
  force = false,
): Promise<CorrectCheckOutcome> {
  const call = httpsCallable<
    {
      orgId: string; checkId: string
      correction: CorrectionPayload
      force?: boolean
    },
    CorrectCheckOutcome
  >(functions, 'correctAndRecheck')
  try {
    const res = await call({ orgId, checkId, correction, ...(force ? { force } : {}) })
    return res.data
  } catch (err) {
    // `failed-precondition` is the backend saying the correction was accepted
    // but unusable (unresolvable payer, missing provider/config) — a different
    // thing from a transport failure, and the only one worth keeping the
    // operator's typed values on screen for.
    if ((err as FunctionsError)?.code === 'functions/failed-precondition') {
      throw new CorrectCheckUnrunnable((err as Error).message)
    }
    throw err
  }
}
